import { useEffect } from 'react';
import { useStore } from 'react-redux';

import {
  type AmplitudeClientHandler,
  type AmplitudeEnvironment,
  type AmplitudeInitOptions,
  getAmplitudeClient,
} from '@repo/amplitude/client';
import { AnalyticsBrowser } from '@segment/analytics-next';

import { getEventContext } from 'contexts/analytics/eventContext';
import { getSegmentAnonymousId } from 'contexts/analytics/segmentAnonymousId';
import type { RootState } from 'store/store';

// Events tracked before init() are queued by the SDK, so children can track right away.
export const useAmplitude = (
  analyticsBrowser: AnalyticsBrowser | null,
  { environment, apiKey }: AmplitudeEnvironment,
  {
    enabled = true,
    skipEngagement,
  }: Pick<AmplitudeInitOptions, 'enabled' | 'skipEngagement'> = {},
): AmplitudeClientHandler | null => {
  const store = useStore<RootState>();
  const amplitude = getAmplitudeClient({ environment, apiKey });

  useEffect(() => {
    if (!amplitude || !apiKey || !enabled) return;

    let isCancelled = false;

    // Seeding the device id from Segment keeps existing devices from resetting.
    getSegmentAnonymousId(analyticsBrowser).then(deviceId => {
      if (isCancelled) return;
      amplitude.init(apiKey, {
        deviceId,
        skipEngagement,
        getEventProperties: () =>
          getEventContext(store.getState(), window.location.pathname),
      });
    });

    return () => {
      isCancelled = true;
    };
  }, [amplitude, analyticsBrowser, apiKey, enabled, skipEngagement, store]);

  return amplitude;
};
